/** Shared diet-plan key mapping used by the legacy editor and the v4 builder. */

/** Flattens each food item's `tips` (string, or {headline,subtitle}/{heading,detail} objects) across
 *  a meal option's food items into one deduped list, for `tipsFromRaw` to parse downstream. */
const uniqueOptionTips = (optionData: any[] | undefined): any[] => {
  const all = (optionData || []).flatMap((item) =>
    Array.isArray(item?.tips) ? item.tips : item?.tips ? [item.tips] : [],
  );
  const seen = new Set<string>();
  return all.filter((tip) => {
    const key = typeof tip === "string" ? tip : JSON.stringify(tip);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const MEAL_TARGET_KEYS = ["kcal", "protein_g", "carbs_g", "fats_g"] as const;

/** Plan-detail target: nested `meal_target`, or the same columns on the meal. Absent when neither is set. */
const mealTargetFromRel = (mealData: any) => {
  const nested = mealData?.meal_target;
  const hasNested = nested != null && typeof nested === "object";
  const hasFlat = MEAL_TARGET_KEYS.some((key) => mealData?.[key] != null);
  if (!hasNested && !hasFlat) return {};
  const src = hasNested ? nested : mealData;
  return {
    kcal: src?.kcal ?? null,
    protein_g: src?.protein_g ?? null,
    carbs_g: src?.carbs_g ?? null,
    fats_g: src?.fats_g ?? null,
  };
};

const mealTargetValue = (v: unknown): number | null | undefined => {
  if (v === undefined) return undefined;
  if (v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Copy only keys the caller set. Undefined is omitted so the legacy editor does not clear stored targets. */
const mealTargetPassThrough = (meal: any) => {
  const out: Partial<Record<(typeof MEAL_TARGET_KEYS)[number], number | null>> = {};
  MEAL_TARGET_KEYS.forEach((key) => {
    const v = mealTargetValue(meal?.[key]);
    if (v !== undefined) out[key] = v;
  });
  return out;
};

export const getDietPlanWithOldKeys = (data: any) => {
  if (data?.diet_plan_meal_rel) {
    return {
      diet_name: data?.plan_name,
      diet_plan_id: data?.diet_plans_id,
      patient_id: data?.patient_id,
      start_date: data?.start_date,
      end_date: data?.end_date,
      calories: data?.total_calories,
      actual_calories: data?.actual_calories,
      proteins: data?.protein,
      fat: data?.fat,
      carbs: data?.carbs,
      chief_remarks: data?.chief_remarks,
      status: data?.status,
      meals: data?.diet_plan_meal_rel?.map((mealData: any) => ({
        diet_plan_meal_rel_id: mealData?.diet_plan_meal_rel_id,
        meal_types_id: mealData?.meal_types_id,
        meal_name: mealData?.meal_name || mealData?.meal_type,
        is_hidden: mealData?.is_hidden || mealData?.hide_meal || "N",
        start_time: mealData?.start_time,
        end_time: mealData?.end_time,
        order_no: mealData?.order_no,
        ...mealTargetFromRel(mealData),
        options: (mealData?.options || mealData?.meal_options)?.map(
          (mealOption: any, mealOptionIndex: number) => {
            const uniqueTips = uniqueOptionTips(mealOption?.option_data);

            return {
              order_no: mealOptionIndex + 1,
              options_name:
                mealOption?.options_name || `Option ${mealOptionIndex + 1}`,
              tips: uniqueTips.length ? uniqueTips : mealOption?.tips || [],
              food_items: (
                mealOption?.food_items || mealOption?.option_data
              )?.map((optionData: any) => ({
                calories: optionData?.calories,
                carbs: optionData?.carbs,
                fats: optionData?.fats,
                fatty_acids:
                  optionData?.fatty_acids || optionData?.fatty_acid,
                fibers: optionData?.fibers || optionData?.fiber,
                food_item_id: optionData?.food_item_id,
                food_item_name:
                  optionData?.food_item_name || optionData?.food_name,
                meal_taken: optionData?.meal_taken,
                order_no: optionData?.order_no,
                potassium: optionData?.potassium,
                protein: optionData?.protein,
                quantity: optionData?.quantity,
                sodium: optionData?.sodium,
                sugar: optionData?.sugar,
                measure_id:
                  optionData?.measure_id || optionData?.unit_option_id,
                measure_name:
                  optionData?.measure_name || optionData?.unit_option_name,
                is_deleted: optionData?.is_deleted || "N",
              })),
            };
          },
        ),
      })),
    };
  }

  return {
    diet_name: data?.plan_name,
    diet_plan_id: data?.diet_plans_id,
    patient_id: data?.patient_id,
    start_date: data?.start_date,
    end_date: data?.end_date,
    calories: data?.total_calories,
    actual_calories: data?.actual_calories,
    proteins: data?.protein,
    fat: data?.fat,
    carbs: data?.carbs,
    chief_remarks: data?.chief_remarks,
    status: data?.status,
    meals: data?.diet_plan_meal_rel?.map((mealData: any) => ({
      diet_plan_meal_rel_id: mealData?.diet_plan_meal_rel_id,
      meal_types_id: mealData?.meal_types_id,
      meal_name: mealData?.meal_type,
      is_hidden: mealData?.hide_meal,
      start_time: mealData?.start_time,
      end_time: mealData?.end_time,
      order_no: mealData?.order_no,
      ...mealTargetFromRel(mealData),
      options: mealData?.meal_options?.map(
        (mealOption: any, mealOptionIndex: number) => {
          const uniqueTips = uniqueOptionTips(mealOption?.option_data);

          return {
            order_no: mealOptionIndex,
            options_name: `Option ${mealOptionIndex + 1}`,
            tips: uniqueTips,
            food_items: mealOption?.option_data?.map((optionData: any) => ({
              calories: optionData?.calories,
              carbs: optionData?.carbs,
              fats: optionData?.fats,
              fatty_acids: optionData?.fatty_acid,
              fibers: optionData?.fiber,
              food_item_id: optionData?.food_item_id,
              food_item_name: optionData?.food_name,
              meal_taken: optionData?.meal_taken,
              order_no: optionData?.order_no,
              potassium: optionData?.potassium,
              protein: optionData?.protein,
              quantity: optionData?.quantity,
              sodium: optionData?.sodium,
              sugar: optionData?.sugar,
              measure_id: optionData?.unit_option_id,
              measure_name: optionData?.unit_option_name,
              is_deleted: optionData?.is_deleted || "N",
            })),
          };
        },
      ),
    })),
  };
};

export const getDietPlanWithNewKeys = (data: any, diet_plan_id?: any) => {
  const dietPlan: any = {
    plan_name: data?.diet_name,
    patient_id: data?.patient_id,
    start_date: data?.start_date,
    end_date: data?.end_date,
    total_calories: data?.calories,
    actual_calories: data?.actual_calories,
    protein: data?.proteins,
    fat: data?.fat,
    carbs: data?.carbs,
    status: data?.status === "Published" ? "approved" : data?.status,
    meals_data: data?.meals?.map((meal: any) => ({
      diet_plan_meal_rel_id: meal?.diet_plan_meal_rel_id,
      meal_types_id: meal?.meal_types_id,
      meal_name: meal?.meal_name,
      hide_meal: meal?.is_hidden,
      start_time: meal?.start_time,
      end_time: meal?.end_time,
      order_no: meal?.order_no,
      ...mealTargetPassThrough(meal),
      options_data: meal?.options?.flatMap((option: any) =>
        (option.food_items || []).map((food_item: any, food_item_index: number) => ({
          tips: option?.tips,
          option_no:
            option?.options_name?.split(" ")[1] ||
            option?.option_no ||
            option?.order_no,
          food_item_id: food_item.food_item_id,
          food_name: food_item.food_item_name,
          quantity: food_item.quantity,
          unit_option_id: food_item.measure_id,
          order_no: option?.order_no || food_item_index + 1,
        })),
      ),
    })),
  };

  if (diet_plan_id) dietPlan["diet_plans_id"] = diet_plan_id;

  return dietPlan;
};
